import React, { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import './ServiceCardsCarousel.css'
import { useServiceData } from '../../hooks/useServiceData'
import car1 from '../../asset/main-slider/car1.avif'
import car2 from '../../asset/main-slider/car2.avif'
import car3 from '../../asset/main-slider/car3.avif'
import car4 from '../../asset/main-slider/car4.avif'
import car5 from '../../asset/main-slider/car5.avif'
import car6 from '../../asset/main-slider/car6.avif'

const cardVisuals = [
  { image: car1, carStyle: { '--car-right': '-34%', '--car-width': '98%' } },
  { image: car2, carStyle: { '--car-right': '-36%', '--car-width': '102%' } },
  { image: car3, carStyle: { '--car-right': '-33%', '--car-width': '96%' } },
  { image: car4, carStyle: { '--car-right': '-37%', '--car-width': '104%' } },
  { image: car5, carStyle: { '--car-right': '-34%', '--car-width': '98%' } },
  { image: car6, carStyle: { '--car-right': '-38%', '--car-width': '102%' } },
]

const ServiceCardItem = ({ card }) => {
  const [isLoaded, setIsLoaded] = useState(false)

  return (
    <Link
      to={card.to}
      className="service-cards-carousel__card"
      style={card.carStyle}
    >
      <h3 className="service-cards-carousel__title">{card.title}</h3>
      <span className="service-cards-carousel__more">
        Подробнее <span aria-hidden="true">→</span>
      </span>
      <img
        className={`service-cards-carousel__car img-fade-in ${isLoaded ? 'is-loaded' : ''}`}
        src={card.image}
        alt=""
        aria-hidden="true"
        onLoad={() => setIsLoaded(true)}
      />
    </Link>
  )
}

const ServiceCardsCarousel = () => {
  const { slug } = useParams()
  const [activePage, setActivePage] = useState(0)

  const { data: service, isLoading } = useServiceData(slug)

  const getCardsPerView = () => {
    if (typeof window === 'undefined') return 3
    if (window.innerWidth < 640) return 1
    if (window.innerWidth < 980) return 2
    return 3
  }
  const [cardsPerView, setCardsPerView] = useState(getCardsPerView)

  const cards = useMemo(
    () =>
      (service?.subServices || []).map((subItem, index) => {
        const visual = cardVisuals[index % cardVisuals.length]
        return {
          ...subItem,
          image: visual.image,
          carStyle: visual.carStyle,
          to: `/services/${slug}/${subItem.slug}`,
        }
      }),
    [service, slug]
  )

  const totalPages = Math.max(Math.ceil(cards.length / cardsPerView), 1)
  const maxPage = Math.max(totalPages - 1, 0)
  const safeActivePage = Math.min(Math.max(activePage, 0), maxPage)
  const offsetCards = safeActivePage * cardsPerView

  useEffect(() => { setActivePage(0) }, [slug, cards.length, cardsPerView])

  useEffect(() => {
    const handleResize = () => setCardsPerView(getCardsPerView())
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (activePage >= totalPages && totalPages > 0) setActivePage(totalPages - 1)
  }, [activePage, totalPages])

  if (!isLoading && cards.length === 0) return null

  const handleNext = () => setActivePage((prev) => Math.min(prev + 1, maxPage))
  const handlePrev = () => setActivePage((prev) => Math.max(prev - 1, 0))

  return (
    <section className="service-cards-carousel" aria-label="Подуслуги" aria-busy={isLoading}>
      <div className="service-cards-carousel__inner">
        <div className="service-cards-carousel__viewport">
          <div
            className="service-cards-carousel__track"
            style={{ '--cards-per-view': cardsPerView, '--offset-cards': isLoading ? 0 : offsetCards }}
          >
            {isLoading
              ? Array.from({ length: cardsPerView }).map((_, index) => (
                  <div
                    className="service-cards-carousel__card service-cards-carousel__card--skeleton img-skeleton"
                    key={`scc-skeleton-${index}`}
                    aria-hidden="true"
                  />
                ))
              : cards.map((card) => <ServiceCardItem key={card.slug} card={card} />)}
          </div>
        </div>

        {!isLoading && (
          <div className="service-cards-carousel__controls">
            <div className="service-cards-carousel__indicator" aria-label="Состояние слайдера">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  type="button"
                  key={`dot-${index}`}
                  onClick={() => setActivePage(index)}
                  aria-label={`Перейти к слайду ${index + 1}`}
                  aria-current={index === safeActivePage ? 'true' : 'false'}
                  className={`service-cards-carousel__dot ${index === safeActivePage ? 'is-active' : ''}`}
                />
              ))}
            </div>

            <div className="service-cards-carousel__arrows">
              <button
                type="button"
                className="service-cards-carousel__arrow"
                onClick={handlePrev}
                aria-label="Предыдущая карточка"
                disabled={safeActivePage === 0}
              >
                <ChevronLeft size={18} strokeWidth={2.4} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="service-cards-carousel__arrow"
                onClick={handleNext}
                aria-label="Следующая карточка"
                disabled={safeActivePage === maxPage}
              >
                <ChevronRight size={18} strokeWidth={2.4} aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default ServiceCardsCarousel
